import React, { useMemo, useCallback } from "react";

import { pick, pluck } from "ramda";

import { useAvailableCountries, useCountriesData, useStatesData} from "lib/config";

import { useRouteMatch } from "react-router-dom";

import MobilityRanking from "components/MobilityRanking";

import useSWR from "swr";
import { jsonFetch } from "lib/fetcher";
import { useTranslation } from "react-i18next";

import {
  sub as subDate,
  isBefore as isDateBefore,
  parseISO,
  formatISO,
} from "date-fns";

import {
  mergeWithKey,
  mapObjIndexed,
  slice,
  descend,
  ascend,
  values,
  sort,
  compose,
  prop,
} from "ramda";


import {
  urls as choroplethUrls,
  useCountryChoropleth,
  useStateChoropleth,
  useWorldChoropleth,
} from "lib/api/distancing/choropleth";

function isAfter(d1, d2) {
  return !isDateBefore(parseISO(d1), parseISO(d2));
}

const getPrevWeek = (d) => {
  const res = subDate(parseISO(d), {
    days: 7,
  });
  return formatISO(res, { representation: "date" });
};

function calculateRanking(type, limit, getName, getUrl) {
  const sortOrder = type === "top" ? ascend : descend;
  return compose(
    slice(0, limit),
    sort(sortOrder(prop("mobilityChange"))),
    values,
    mapObjIndexed((value, key) => ({
      ...value,
      name: getName(key),
      url: getUrl ? getUrl(key) : null,
    })),
    mergeWithKey((key, current, prev) => ({
      ...current,
      key,
      lastChange:
        current.mobilityChange -
        prev.mobilityChange,
    }))
  );
}

function BaseMobilityRanking({
  type,
  getName,
  getUrl,
  limit,
  data,
  prevData,
  error,
  prevError,
}) {
  const { t } = useTranslation();

  const rankingData = useMemo(
    () =>
      data &&
      calculateRanking(type, limit, getName, getUrl)(data, prevData || []),
    [data, prevData, type, limit, getName, getUrl]
  );

  if (error || prevError) {
    return <div>{error?.message || prevError?.message}</div>;
  }

  const title =
    type === "top"
      ? t("mobilityRankings.top.title")
      : t("mobilityRankings.bottom.title");
  return <MobilityRanking data={rankingData} title={title} />;
}

/**
 * World Ranking
 */
function BaseWorldMobilityRanking({ type, selectedDate, baselineDate, limit }) {
  const { url } = useRouteMatch();

  const { data, error } = useWorldChoropleth(true, selectedDate, baselineDate);

  const prevWeek = getPrevWeek(selectedDate);

  const { data: prevData, error: prevError } = useWorldChoropleth(
    isAfter(prevWeek, baselineDate),
    prevWeek,
    baselineDate
  );

  const availableCountries = useAvailableCountries();

  const availableCountriesData = useMemo(
    () => pick(pluck("id")(availableCountries), data?.content || []),
    [data, availableCountries]
  );

  const availableCountriesPrevData = useMemo(
    () => pick(pluck("id")(availableCountries), prevData?.content || []),
    [prevData, availableCountries]
  );

  const getUrl = useCallback((key) => `${url}/${key}`, [url]);

  const countriesData = useCountriesData();

  const getLevel0Name = useCallback((countryId) => {
    return countriesData[countryId].name
  }, [countriesData])

  

  return (
    <BaseMobilityRanking
      getName={getLevel0Name}
      getUrl={getUrl}
      type={type}
      limit={limit}
      data={availableCountriesData}
      prevData={availableCountriesPrevData}
      error={error}
      prevError={prevError}
    />
  );
}

/**
 * Country Ranking
 */
function BaseCountryMobilityRanking({
  type,
  selectedDate,
  baselineDate,
  country,
  limit,
}) {
  const { url } = useRouteMatch();

  const { data, error } = useCountryChoropleth(
    true,
    country,
    selectedDate,
    baselineDate
  );
  
  const prevWeek = getPrevWeek(selectedDate);

  const { data: prevData, error: prevError } = useCountryChoropleth(
    isAfter(prevWeek, baselineDate),
    country,
    prevWeek,
    baselineDate
  );

  const statesData = useStatesData(country);

  const getName = useCallback((key) => {
    // if (!statesData[key]) debugger;
    return statesData[key].name;
  }, [statesData]);
  const getUrl = useCallback((key) => `${url}/${country}/${key}`, [
    url,
    country,
  ]);

  return (
    <BaseMobilityRanking
      getName={getName}
      getUrl={getUrl}
      type={type}
      limit={limit}
      data={data?.content}
      prevData={prevData?.content}
      error={error}
      prevError={prevError}
    />
  );
}

/**
 * State Ranking
 *
 */
function BaseStateMobilityRanking({
  type,
  selectedDate,
  baselineDate,
  country,
  state,
  limit,
}) {
  const { data: stateGeoJsonData, error: stateGeoJsonError } = useSWR(
    choroplethUrls.getStateGeoJson(country, state),
    jsonFetch,
    {
      suspense: false,
    }
  );

  const { data, error } = useStateChoropleth(
    true,
    country,
    state,
    selectedDate,
    baselineDate
  );

  const prevWeek = getPrevWeek(selectedDate);

  const { data: prevData, error: prevError } = useStateChoropleth(
    isAfter(prevWeek, baselineDate),
    country,
    state,
    prevWeek,
    baselineDate
  );

  const names = useMemo(() => {
    const names = {};

    if (!stateGeoJsonData) {
      return names;
    }

    for (let feature of stateGeoJsonData.features) {
      names[feature.properties["ID_2"]] = feature.properties["NAME_2"];
    }

    return names;
  }, [stateGeoJsonData]);

  const getName = useCallback(
    (key) => {
      return names[key];
    },
    [names]
  );

  // Should center map in this case.
  // const getUrl = useCallback((key) => `${country}/${key}`, [country]);

  if (stateGeoJsonError) {
    return <div>{stateGeoJsonError.message}</div>;
  }

  return (
    <BaseMobilityRanking
      getName={getName}
      type={type}
      limit={limit}
      data={data?.content}
      prevData={prevData?.content}
      error={error}
      prevError={prevError}
    />
  );
}

export default function ({ type, level, ...props }) {
  if (level === "world") {
    return <BaseWorldMobilityRanking limit={5} type={type} {...props} />;
  } else if (level === "country") {
    return <BaseCountryMobilityRanking limit={5} type={type} {...props} />;
  } else if (level === "state" || level === "county") {
    return <BaseStateMobilityRanking limit={5} type={type} {...props} />;
  } else {
    throw new Error(`Unknown level ${level}`)
  }
}
